import axios from 'axios';
import jobService, { Job } from './jobService';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests when available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface Employer {
  id: string;
  companyName: string;
  businessName?: string;
  companyDescription: string;
  industry: string;
  companySize: string;
  website?: string;
  logo?: string;
  location: string;
  foundedYear?: number;
  employeeCount?: string;
  benefits?: string[];
  culture?: string;
  values?: string[];
  socialMedia?: {
    linkedin?: string;
    twitter?: string;
    facebook?: string;
  };
  createdAt: Date;
  updatedAt: Date;
  isVerified: boolean;
  jobsPosted: number;
  activeJobs: number;
  averageRating?: number;
  totalReviews?: number;
  jobPostingStats?: {
    activeJobs: number;
    totalJobs: number;
    totalApplications: number;
    hiredCandidates: number;
    averageApplicationsPerJob: number;
  };
  hiringStats?: {
    averageTimeToHire: number;
    successRate: number;
    candidateRating: number;
    repeatApplicants: number;
    responseRate?: number;
  };
}

export interface EmployerWithJobs extends Employer {
  jobs: Job[];
  recentJobs: Job[];
}

class EmployerService {
  // Helper method to generate domain from company name
  private generateDomainFromCompanyName(companyName: string): string {
    return companyName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '') // Remove spaces
      .replace(/(inc|llc|ltd|corp|corporation|company|co)$/i, '') // Remove company suffixes
      + '.com';
  }

  // Get all employers from the database
  async getEmployers(filters?: {
    industry?: string;
    companySize?: string;
    location?: string;
    hasActiveJobs?: boolean;
    limit?: number;
  }): Promise<{ employers: Employer[]; total: number }> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== '') {
            params.append(key, value.toString());
          }
        });
      }
      
      // Get employer profiles directly from the profiles endpoint
      let response;
      try {
        response = await api.get(`/profiles/employers?${params.toString()}`);
      } catch (error) {
        console.log('Profiles endpoint failed, using fallback');
        response = { data: { data: [] } };
      }
      
      // Backend returns {success: true, data: [...]} format
      const employers = response.data?.data || response.data || [];
      
      // Ensure employers is an array
      const employersArray = Array.isArray(employers) ? employers : [];
      
      // Transform the data to match our Employer interface
      const transformedEmployers = employersArray
        .filter((emp: any) => emp && emp.companyName && emp.companyName.trim().length > 0) // Only valid company names
        .map((emp: any) => {
          // Check for logo from various fields in the employer profile
          let logo = emp.companyLogo || emp.logo || null;
          
          return {
            id: emp.id,
            companyName: emp.companyName || 'Unknown Company',
            companyDescription: emp.description || 'No description available',
            industry: emp.industry || 'Other',
            companySize: emp.companySize || 'Unknown',
            location: emp.location || 'Remote',
            website: emp.website || '',
            logo: logo,
            employeeCount: emp.employeeCount || 'Unknown',
            foundedYear: emp.foundedYear,
            benefits: [],
            isVerified: emp.isVerified || false,
            jobsPosted: 0, // Will be calculated later
            activeJobs: 0, // Will be calculated later
            averageRating: 4.0,
            totalReviews: 0,
            createdAt: emp.createdAt ? new Date(emp.createdAt._seconds * 1000) : new Date(),
            updatedAt: emp.updatedAt ? new Date(emp.updatedAt._seconds * 1000) : new Date()
          };
        });
      
      console.log('Raw API response:', Array.isArray(employers) ? employers.slice(0, 2) : employers);
      console.log('Transformed employers:', transformedEmployers.slice(0, 5).map((e: Employer) => ({ 
        id: e.id, 
        name: e.companyName,
        logo: e.logo 
      })));
      
      console.log('Raw employer data with logos:', employersArray.slice(0, 3).map((emp: any) => ({
        id: emp.id,
        companyName: emp.companyName,
        logo: emp.logo,
        companyLogo: emp.companyLogo
      })));
      
      return {
        employers: transformedEmployers,
        total: transformedEmployers.length
      };
    } catch (error) {
      console.error('Error fetching employers from all endpoints:', error);
      // Return empty data instead of throwing to prevent app crashes
      return { employers: [], total: 0 };
    }
  }

  // Get employer by ID
  async getEmployerById(employerId: string): Promise<Employer> {
    try {
      const response = await api.get(`/profiles/employers/${employerId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching employer:', error);
      throw new Error('Failed to fetch employer');
    }
  }

  // Get top employers (most active or highest rated)
  async getTopEmployers(limit: number = 6): Promise<Employer[]> {
    try {
      const response = await this.getEmployers({ 
        hasActiveJobs: true,
        limit: limit * 2 // Get more to filter and sort
      });
      
      // Sort by active jobs count and rating
      return response.employers
        .filter(employer => employer.activeJobs > 0)
        .sort((a, b) => {
          // Primary sort: active jobs
          const jobDiff = b.activeJobs - a.activeJobs;
          if (jobDiff !== 0) return jobDiff;
          
          // Secondary sort: rating
          const aRating = a.averageRating || 0;
          const bRating = b.averageRating || 0;
          return bRating - aRating;
        })
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching top employers:', error);
      return []; // Return empty array to prevent crashes
    }
  }

  // Get employers with their active jobs, using real employer_profiles
  async getEmployersWithJobs(limit: number = 6): Promise<EmployerWithJobs[]> {
    try {
      // First get all employer profiles from the database
      const employersResponse = await this.getEmployers({ limit: 100 });
      const allEmployerProfiles = employersResponse.employers;
      
      if (allEmployerProfiles.length === 0) {
        console.log('No employer profiles found, using fallback');
        return this.getMockTopEmployers();
      }
      
      // Get jobs and match them to employer profiles
      const allJobsResponse = await jobService.getJobs({ limit: 500 });
      const allJobs = allJobsResponse.jobs;
      
      // Group jobs by employer ID to count jobs per employer
      const employerJobCounts = allJobs.reduce((acc, job) => {
        const employerId = job.employerId;
        
        // Skip jobs without valid employer ID
        if (!employerId) {
          return acc;
        }
        
        if (!acc[employerId]) {
          acc[employerId] = {
            employerId: employerId,
            jobs: [],
            jobCount: 0
          };
        }
        acc[employerId].jobs.push(job);
        acc[employerId].jobCount++;
        return acc;
      }, {} as Record<string, { employerId: string; jobs: any[]; jobCount: number }>);
      
      // Match employer profiles with their job counts
      const employersWithJobData = allEmployerProfiles
        .map(employer => {
          const jobData = employerJobCounts[employer.id];
          return {
            ...employer,
            jobs: jobData?.jobs || [],
            recentJobs: jobData?.jobs?.slice(0, 5) || [],
            activeJobs: jobData?.jobCount || 0,
            jobsPosted: jobData?.jobCount || 0
          };
        })
        .filter(employer => {
          // Only include employers with valid company names and at least 1 job
          return employer.companyName && 
                 employer.companyName.trim().length > 2 &&
                 !['Unknown Company', 'You', 'We', 'I', 'They'].includes(employer.companyName.trim()) &&
                 employer.activeJobs > 0;
        })
        .sort((a, b) => b.activeJobs - a.activeJobs) // Sort by job count
        .slice(0, limit);
      
      console.log('Top employers from profiles:', employersWithJobData.map((e: EmployerWithJobs) => ({ 
        name: e.companyName, 
        jobs: e.activeJobs,
        id: e.id 
      })));
      
      // If we don't have enough real employers, fill with fallback
      if (employersWithJobData.length < 3) {
        console.log('Not enough real employers, adding fallback data');
        const fallbackEmployers = this.getMockTopEmployers().slice(0, limit - employersWithJobData.length);
        return [...employersWithJobData, ...fallbackEmployers];
      }
      
      return employersWithJobData;
    } catch (error) {
      console.error('Error fetching employers with jobs:', error);
      return this.getMockTopEmployers();
    }
  }

  // Helper methods for employer data generation
  private generateCompanyDescription(companyName: string): string {
    const descriptions = {
      'tech': 'Leading technology solutions company',
      'data': 'Advanced data analytics and insights',
      'design': 'Creative design and user experience',
      'finance': 'Financial services and solutions',
      'health': 'Healthcare technology and innovation',
      'education': 'Educational technology and services',
      'retail': 'E-commerce and retail solutions',
      'energy': 'Clean energy and sustainability'
    };
    
    const lowerName = companyName.toLowerCase();
    for (const [key, desc] of Object.entries(descriptions)) {
      if (lowerName.includes(key)) return desc;
    }
    return 'Innovative solutions and services';
  }

  private inferIndustryFromJobs(jobs: any[]): string {
    const industries = jobs.map(job => job.industryCategory || job.category).filter(Boolean);
    if (industries.length === 0) return 'Technology';
    
    // Return most common industry
    const industryCounts = industries.reduce((acc, industry) => {
      acc[industry] = (acc[industry] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(industryCounts)
      .sort(([,a], [,b]) => (b as number) - (a as number))[0]?.[0] || 'Technology';
  }

  private getMostCommonLocation(jobs: any[]): string {
    const locations = jobs.map(job => job.location).filter(Boolean);
    if (locations.length === 0) return 'Remote';
    
    const locationCounts = locations.reduce((acc, location) => {
      acc[location] = (acc[location] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(locationCounts)
      .sort(([,a], [,b]) => (b as number) - (a as number))[0]?.[0] || 'Remote';
  }

  // Get employers by industry
  async getEmployersByIndustry(industry: string, limit: number = 10): Promise<Employer[]> {
    try {
      const response = await this.getEmployers({ 
        industry, 
        hasActiveJobs: true,
        limit 
      });
      return response.employers;
    } catch (error) {
      console.error('Error fetching employers by industry:', error);
      return [];
    }
  }

  // Get employer statistics
  async getEmployerStats(): Promise<{
    totalEmployers: number;
    industries: string[];
    companySizes: string[];
    topLocations: string[];
    averageJobsPerEmployer: number;
  }> {
    try {
      const response = await api.get('/profiles/employers/stats');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching employer stats:', error);
      return {
        totalEmployers: 0,
        industries: [],
        companySizes: [],
        topLocations: [],
        averageJobsPerEmployer: 0
      };
    }
  }

  // Search employers
  async searchEmployers(query: string): Promise<Employer[]> {
    try {
      const response = await api.get(`/profiles/employers/search?q=${encodeURIComponent(query)}`);
      return response.data.data || [];
    } catch (error) {
      console.error('Error searching employers:', error);
      return [];
    }
  }

  // Get featured employers (if we have such a feature)
  async getFeaturedEmployers(limit: number = 6): Promise<Employer[]> {
    try {
      // For now, return top employers as featured
      return this.getTopEmployers(limit);
    } catch (error) {
      console.error('Error fetching featured employers:', error);
      return [];
    }
  }

  // Create mock employer data for development/fallback
  getMockTopEmployers(): EmployerWithJobs[] {
    return [
      {
        id: '1',
        companyName: 'TechGlobal Inc.',
        companyDescription: 'Leading technology solutions company',
        industry: 'Technology',
        companySize: '1000-5000',
        location: 'San Francisco, CA',
        website: 'https://techglobal.com',
        logo: 'https://logo.clearbit.com/techglobal.com',
        employeeCount: '10,000+',
        foundedYear: 2010,
        isVerified: true,
        jobsPosted: 150,
        activeJobs: 25,
        averageRating: 4.5,
        totalReviews: 89,
        createdAt: new Date('2023-01-01'),
        updatedAt: new Date(),
        jobs: [],
        recentJobs: []
      },
      {
        id: '2',
        companyName: 'FinanceCorps',
        companyDescription: 'Global financial services provider',
        industry: 'Finance',
        companySize: '5000+',
        location: 'New York, NY',
        website: 'https://financecorps.com',
        logo: 'https://logo.clearbit.com/financecorps.com',
        employeeCount: '50,000+',
        foundedYear: 1985,
        isVerified: true,
        jobsPosted: 200,
        activeJobs: 35,
        averageRating: 4.2,
        totalReviews: 156,
        createdAt: new Date('2023-01-01'),
        updatedAt: new Date(),
        jobs: [],
        recentJobs: []
      },
      {
        id: '3',
        companyName: 'HealthTech Solutions',
        companyDescription: 'Healthcare technology innovation',
        industry: 'Healthcare',
        companySize: '1000-5000',
        location: 'Boston, MA',
        website: 'https://healthtech.com',
        logo: 'https://logo.clearbit.com/healthtech.com',
        employeeCount: '5,000+',
        foundedYear: 2015,
        isVerified: true,
        jobsPosted: 75,
        activeJobs: 18,
        averageRating: 4.7,
        totalReviews: 42,
        createdAt: new Date('2023-01-01'),
        updatedAt: new Date(),
        jobs: [],
        recentJobs: []
      }
    ];
  }
}

const employerServiceInstance = new EmployerService();
export default employerServiceInstance;